import { AddExistingGuests } from '@common/components/AddExistingGuests';
import { useCallback } from 'react';
import { UseFieldArrayReturn, useFormContext } from 'react-hook-form';
import { Guest } from 'shared-types/Basket';
import { RoomRateFormValues } from '../RoomRateFormV2.schema';

type GuestFieldArray = UseFieldArrayReturn<RoomRateFormValues, 'guests', 'id'>;
type GuestEntry = RoomRateFormValues['guests'][number];

type AddExistingGuestsV2Props = {
  fieldArray: GuestFieldArray;
  guestValues: RoomRateFormValues['guests'];
  selectedGuests: Guest[];
  existingGuests: Guest[];
  guest: Guest | null;
  minimumGuests?: number;
  maximumGuests?: number;
  isGenderSpecific?: string | boolean;
};

export const AddExistingGuestsV2 = ({
  fieldArray,
  guestValues,
  selectedGuests,
  existingGuests,
  guest,
  minimumGuests,
  maximumGuests,
  isGenderSpecific = false,
}: AddExistingGuestsV2Props) => {
  const { append, remove, insert } = fieldArray;
  const { trigger } = useFormContext<RoomRateFormValues>();

  const triggerGuests = useCallback(() => {
    setTimeout(() => trigger('guests'), 0);
  }, [trigger]);

  const fields = {
    push: (value: GuestEntry) => {
      append(value);
      triggerGuests();
    },
    remove: (index: number) => {
      remove(index);
      triggerGuests();
    },
    insert: (index: number, value: GuestEntry) => {
      insert(index, value);
      triggerGuests();
    },
    get: (index: number) => guestValues?.[index],
    getAll: () => guestValues ?? [],
    length: guestValues?.length ?? 0,
    pop: () => {
      const len = guestValues?.length ?? 0;
      if (len > 0) {
        remove(len - 1);
        triggerGuests();
      }
    },
  };

  return (
    <AddExistingGuests
      fields={fields}
      selectedGuests={selectedGuests}
      existingGuests={existingGuests}
      guest={guest as Guest}
      minimumGuests={minimumGuests}
      maximumGuests={maximumGuests}
      isGenderSpecific={isGenderSpecific}
    />
  );
};
